// Configuration utilities for currency, date, and other formats

export interface AppConfig {
  currency: {
    code: string;
    symbol: string;
    locale: string;
    decimalPlaces: number;
  };
  dateTime: {
    locale: string;
    dateFormat: string;
    timeFormat: string;
    timezone: string;
  };
  regional: {
    country: string;
    language: string;
    numberFormat: string;
  };
}

// Default configuration - can be overridden by admin settings
export const defaultConfig: AppConfig = {
  currency: {
    code: 'USD',
    symbol: '$',
    locale: 'en-US',
    decimalPlaces: 2,
  },
  dateTime: {
    locale: 'en-US',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: '12h',
    timezone: 'America/New_York',
  },
  regional: {
    country: 'US',
    language: 'en',
    numberFormat: 'en-US',
  },
};

// Get current configuration (will be enhanced to fetch from API/admin settings)
export const getAppConfig = (): AppConfig => {
  // TODO: Fetch from admin configuration API
  const savedConfig = localStorage.getItem('appConfig');
  if (savedConfig) {
    try {
      return { ...defaultConfig, ...JSON.parse(savedConfig) };
    } catch {
      return defaultConfig;
    }
  }
  return defaultConfig;
};

// Currency formatting
export const formatCurrency = (amount: number, config?: AppConfig): string => {
  const cfg = config || getAppConfig();

  return new Intl.NumberFormat(cfg.currency.locale, {
    style: 'currency',
    currency: cfg.currency.code,
    minimumFractionDigits: cfg.currency.decimalPlaces,
    maximumFractionDigits: cfg.currency.decimalPlaces,
  }).format(amount);
};

// Parse currency input (remove formatting and return number)
export const parseCurrencyInput = (value: string): number => {
  if (!value || typeof value !== 'string') return 0;

  // Remove currency symbols, commas, spaces, and other non-numeric characters
  // Keep only digits, decimal points, and minus signs
  const cleanValue = value
    .replace(/[$€£¥₹₽¢]/g, '') // Remove common currency symbols
    .replace(/[,\s]/g, '') // Remove commas and spaces
    .replace(/[^0-9.-]/g, '') // Remove everything except digits, decimal point, and minus
    .replace(/-+/g, '-') // Replace multiple minus signs with single minus
    .replace(/\.+/g, '.'); // Replace multiple decimal points with single decimal

  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Format number input for currency fields
export const formatCurrencyInput = (value: number): string => {
  return value.toFixed(2);
};

// Date formatting
export const formatDate = (date: Date | string, config?: AppConfig): string => {
  const cfg = config || getAppConfig();
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(cfg.dateTime.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: cfg.dateTime.timezone,
  }).format(dateObj);
};

// Get minimum date for date inputs (today)
export const getMinDate = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Validate date is not in the past
export const isDateInPast = (date: Date | string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};
